import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Card,
  CardBody,
  CardHead,
  CertificateCard,
  EmptyState,
  Icon,
  Pill,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { usePermission } from "@/auth/usePermission";
import { SplashScreen } from "@/app/SplashScreen";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import {
  useClient,
  useClientCertificate,
  useClientContacts,
  useDeleteClient,
  useDeleteContact,
  useRevokeClientCertificate,
  useUploadClientCertificate,
} from "@/hooks/useClients";
import { getInitials } from "@/utils/initials";
import type { ClientContact } from "@/types/client";
import { ClientFinanceTab } from "./ClientFinanceTab";
import { ClientFormModal } from "./ClientFormModal";
import { ContactFormModal } from "./ContactFormModal";
import "./ClientDetail.css";

type Tab = "dados" | "financeiro" | "contatos";

export function ClientDetail() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const navigate = useNavigate();
  const clientId = Number(useParams().id);
  const { data: client, isLoading, isError } = useClient(activeWorkspaceId, clientId);
  const { data: contacts = [] } = useClientContacts(activeWorkspaceId, clientId);
  const del = useDeleteClient(activeWorkspaceId ?? 0);
  const delContact = useDeleteContact(activeWorkspaceId ?? 0, clientId);
  const [editing, setEditing] = useState(false);
  const [contactForm, setContactForm] = useState<{ open: boolean; contact?: ClientContact }>({
    open: false,
  });
  const [tab, setTab] = useState<Tab>("dados");
  const [actionError, setActionError] = useState<string | null>(null);

  if (!Number.isFinite(clientId)) return <Navigate to="/empresas" replace />;
  if (isLoading) return <SplashScreen />;
  if (isError || !client || activeWorkspaceId == null) return <Navigate to="/empresas" replace />;

  async function onDelete() {
    if (!confirm("Excluir esta empresa?")) return;
    setActionError(null);
    try {
      await del.mutateAsync(clientId);
      navigate("/empresas");
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  async function onDeleteContact(id: number) {
    if (!confirm("Remover este contato?")) return;
    setActionError(null);
    try {
      await delContact.mutateAsync(id);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  return (
    <>
      <button className="detail-back" onClick={() => navigate("/empresas")}>
        <Icon name="arrow-left" size={15} />
        Empresas
      </button>

      {actionError && (
        <div className="form-error" role="alert" style={{ marginBottom: 12 }}>
          {actionError}
        </div>
      )}

      <div className="detail-hero">
        <span className="org-mono-mark xl">{getInitials(client.legal_name)}</span>
        <div className="detail-hero-info">
          <div className="detail-hero-info-head">
            <h1 className="page-title" style={{ margin: 0 }}>
              {client.legal_name}
            </h1>
            <Pill tone={client.active ? "ativa" : "pausada"}>
              {client.active ? "Ativa" : "Inativa"}
            </Pill>
            {client.is_matriz && <Pill tone="diag">Matriz</Pill>}
            {!client.is_matriz && client.parent_client_id != null && (
              <Pill tone="diag">Filial</Pill>
            )}
          </div>
          <p className="page-sub" style={{ marginTop: 4 }}>
            {client.document_type === "cnpj" ? "Pessoa jurídica" : "Pessoa física"} ·{" "}
            {client.document_masked}
          </p>
          <div className="detail-hero-meta">
            <div className="detail-hero-meta-item">
              <div className="l">Documento</div>
              <div className="v">{client.document ?? client.document_masked}</div>
            </div>
            <div className="detail-hero-meta-item">
              <div className="l">Responsável</div>
              <div className="v">{client.responsible_name ?? "—"}</div>
            </div>
            <div className="detail-hero-meta-item">
              <div className="l">CPF do responsável</div>
              <div className="v">
                {client.responsible_cpf ?? client.responsible_cpf_masked ?? "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="detail-hero-actions">
          <Guard permission="clients.write">
            <Button variant="ghost" size="sm" iconLeft="settings" onClick={() => setEditing(true)}>
              Editar
            </Button>
          </Guard>
          <Guard permission="clients.delete">
            <Button
              variant="danger"
              size="sm"
              iconLeft="x"
              onClick={() => void onDelete()}
              disabled={del.isPending}
            >
              Excluir
            </Button>
          </Guard>
        </div>
      </div>

      <div className="detail-tabs">
        <button
          className={`detail-tab ${tab === "dados" ? "active" : ""}`}
          onClick={() => setTab("dados")}
        >
          Dados
        </button>
        <button
          className={`detail-tab ${tab === "financeiro" ? "active" : ""}`}
          onClick={() => setTab("financeiro")}
        >
          Endereço & banco
        </button>
        <button
          className={`detail-tab ${tab === "contatos" ? "active" : ""}`}
          onClick={() => setTab("contatos")}
        >
          Contatos <span className="count">{contacts.length}</span>
        </button>
      </div>

      {tab === "dados" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <CardHead title="Dados cadastrais" />
            <CardBody>
              <dl className="detail-fields">
                <div>
                  <dt>Razão social / Nome</dt>
                  <dd>{client.legal_name}</dd>
                </div>
                <div>
                  <dt>Documento</dt>
                  <dd>{client.document ?? client.document_masked}</dd>
                </div>
                <div>
                  <dt>Tipo</dt>
                  <dd>{client.document_type === "cnpj" ? "PJ (CNPJ)" : "PF (CPF)"}</dd>
                </div>
                <div>
                  <dt>Regime tributário</dt>
                  <dd>{client.tax_regime ?? "—"}</dd>
                </div>
                <div>
                  <dt>Responsável</dt>
                  <dd>{client.responsible_name ?? "—"}</dd>
                </div>
              </dl>
            </CardBody>
          </Card>
          <ClientCertificateCard workspaceId={activeWorkspaceId} clientId={client.id} />
        </div>
      )}

      {tab === "financeiro" && (
        <ClientFinanceTab workspaceId={activeWorkspaceId} client={client} />
      )}

      {tab === "contatos" && (
        <Card>
          <CardHead
            title="Contatos"
            actions={
              <Guard permission="clients.write">
                <Button
                  variant="ghost"
                  iconLeft="plus"
                  onClick={() => setContactForm({ open: true })}
                >
                  Novo contato
                </Button>
              </Guard>
            }
          />
          <CardBody>
            {contacts.length === 0 ? (
              <EmptyState
                icon="users"
                title="Nenhum contato"
                description="Adicione decisores, financeiro, contador…"
              />
            ) : (
              <ul className="contact-rows">
                {contacts.map((c) => (
                  <li key={c.id} className="contact-row">
                    <div className="contact-row-main">
                      <div className="contact-row-name">
                        {c.name}
                        {c.role && <span className="td-small"> · {c.role}</span>}
                        {c.accepts_marketing && <Pill tone="diag">marketing</Pill>}
                      </div>
                      <div className="contact-row-meta">
                        {(c.email ?? c.email_masked) ?? "—"}
                        {(c.phone ?? c.phone_masked) ? ` · ${c.phone ?? c.phone_masked}` : ""}
                      </div>
                    </div>
                    <Guard permission="clients.write">
                      <div className="contact-row-actions">
                        <Button
                          variant="ghost"
                          iconLeft="settings"
                          onClick={() => setContactForm({ open: true, contact: c })}
                        >
                          Editar
                        </Button>
                        <Guard permission="clients.delete">
                          <Button
                            variant="ghost"
                            iconLeft="x"
                            onClick={() => void onDeleteContact(c.id)}
                          >
                            Remover
                          </Button>
                        </Guard>
                      </div>
                    </Guard>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {editing && (
        <ClientFormModal
          mode="edit"
          workspaceId={activeWorkspaceId}
          client={client}
          onClose={() => setEditing(false)}
        />
      )}
      {contactForm.open && (
        <ContactFormModal
          workspaceId={activeWorkspaceId}
          clientId={clientId}
          contact={contactForm.contact}
          onClose={() => setContactForm({ open: false })}
        />
      )}
    </>
  );
}

function ClientCertificateCard({
  workspaceId,
  clientId,
}: {
  workspaceId: number;
  clientId: number;
}) {
  const { data: cert, isLoading } = useClientCertificate(workspaceId, clientId);
  const upload = useUploadClientCertificate(workspaceId, clientId);
  const revoke = useRevokeClientCertificate(workspaceId, clientId);
  const canManage = usePermission("clients.write");
  return (
    <CertificateCard
      cert={cert}
      isLoading={isLoading}
      canManage={canManage}
      onUpload={(file, password) => upload.mutateAsync({ file, password })}
      onRevoke={() => revoke.mutateAsync()}
    />
  );
}
